/**
 * Blocks same-origin POST requests issued straight from the browser console.
 *
 * The cart endpoints (/cart/add.js, /cart/change.js, /cart/update.js) are open
 * to anyone who can type into devtools, which is how the free-gift threshold
 * gets bypassed. This wraps fetch and XMLHttpRequest and refuses POSTs whose
 * call chain did not start inside a script.
 *
 * This is a deterrent, not a security boundary: the console can still reach a
 * pristine fetch through a fresh iframe, and the endpoints stay reachable from
 * curl with a session cookie. Only a server-side rule (Shopify Function / Flow)
 * can actually enforce the gift threshold.
 */
(function () {
  'use strict';

  var TRUSTED_FRAME = /\bhttps?:\/\//;

  /**
   * True when the outermost frame of the current call chain is not a script.
   *
   * Console input has no source file, so the bottom frame reads "at <anonymous>"
   * or "at VM123:1:1" in Chrome, "@debugger eval code" in Firefox and "global
   * code" in Safari. Anything running from a real file — theme assets, app
   * bundles, even an inline <script>, which reports the document URL — bottoms
   * out on an http(s) frame.
   *
   * Reading the bottom frame rather than scanning the whole stack keeps this
   * independent of load order: ct-atc-flow.js also wraps fetch, and its frame
   * would otherwise vouch for a console call that passed through its wrapper.
   */
  function isFromConsole() {
    var stack;
    try {
      stack = new Error().stack;
    } catch (e) {
      return false;
    }
    if (!stack) return false; // No stack to judge by — allow, never break checkout.

    var frames = stack.split('\n').filter(function (line) {
      return line.trim() !== '';
    });
    if (frames.length < 2) return false;

    return !TRUSTED_FRAME.test(frames[frames.length - 1]);
  }

  // Cross-origin traffic belongs to third parties and is left alone; only this
  // store's own endpoints are worth guarding.
  function isSameOrigin(url) {
    try {
      return new URL(String(url), window.location.href).origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  function blocked(method, url) {
    var error = new Error('Blocked: POST requests from the console are not allowed.');
    error.name = 'ConsolePostBlocked';
    if (window.console && console.warn) {
      console.warn('[console-post-guard] blocked ' + method + ' ' + url);
    }
    return error;
  }

  var nativeFetch = window.fetch;

  window.fetch = function (resource, init) {
    var url = (typeof resource === 'string') ? resource : (resource && resource.url) || '';
    var method = (init && init.method) || (resource && resource.method) || 'GET';

    if (String(method).toUpperCase() === 'POST' && isSameOrigin(url) && isFromConsole()) {
      return Promise.reject(blocked('POST', url));
    }
    return nativeFetch.apply(this, arguments);
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  // open() records the request; send() is where the console frame still sits on
  // the stack, so the decision has to be taken there.
  XMLHttpRequest.prototype.open = function (method, url) {
    this.ctGuardMethod = String(method || '').toUpperCase();
    this.ctGuardUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this.ctGuardMethod === 'POST' && isSameOrigin(this.ctGuardUrl) && isFromConsole()) {
      throw blocked('POST', this.ctGuardUrl);
    }
    return nativeSend.apply(this, arguments);
  };
})();
